/**
 * §7.2 檢視者的落點：`/dashboard` **全鏈概況**。
 * 「在庫、回收率、全鏈平衡」
 *
 * §9 規則 13：進庫未過磅時，全鏈分母改用 OP-1 逐片實測合計，**並在報表註明**。
 * 那句註明不是裝飾——看報表的人必須知道這個數字少算了進庫到 OP-1 之間的損耗。
 */

import { download } from '../api/client.js'
import { useQuery } from '../api/hooks.js'
import { Alert, Button, Card, Empty, KV, Table, Td } from '../components/ui.js'
import { useDict } from '../config/ConfigContext.js'
import { kgUnit, pct, count } from '../../../shared/lib/format.js'

interface Chain {
  siteId: string
  intakeKg: number
  intakeFromReceivingKg: number
  intakeFromOp1Kg: number
  denominatorNote: string
  shippedKg: number
  lossKg: number
  recoveryPct: number | null
}

interface Item { item_id: string; material_code: string; net_kg: string | null; panel_count: number | null }

export function ChainDashboard() {
  const dict = useDict()
  const chain = useQuery<Chain>('/api/reports/chain-balance')
  const stock = useQuery<{ items: Item[] }>('/api/inventory/items?limit=500')

  const byMaterial = new Map<string, { kg: number; items: number; panels: number }>()
  for (const it of stock.data?.items ?? []) {
    const e = byMaterial.get(it.material_code) ?? { kg: 0, items: 0, panels: 0 }
    e.kg += it.net_kg === null ? 0 : Number(it.net_kg)
    e.items += 1
    e.panels += it.panel_count ?? 0
    byMaterial.set(it.material_code, e)
  }

  return (
    <div className="space-y-4">
      <Card
        title="全鏈物料平衡"
        right={<Button size="sm" onClick={() => download('/api/reports/chain-balance', { format: 'csv' })}>
          下載 CSV
        </Button>}
      >
        {chain.loading && <p className="text-sm text-ink-500">載入中…</p>}
        {chain.error && <Alert tone="danger" title="讀不到報表">{chain.error.message}</Alert>}
        {chain.data && (
          <>
            <div className="grid gap-3 sm:grid-cols-3 mb-4">
              <Stat label="進料" value={kgUnit(chain.data.intakeKg, 1)} />
              <Stat label="已出廠" value={kgUnit(chain.data.shippedKg, 1)} />
              <Stat
                label="回收率"
                value={chain.data.recoveryPct === null ? '—' : pct(chain.data.recoveryPct)}
                tone="sky"
              />
            </div>
            <KV items={[
              ['其中：進庫過磅', kgUnit(chain.data.intakeFromReceivingKg, 1)],
              ['其中：OP-1 逐片實測', kgUnit(chain.data.intakeFromOp1Kg, 1)],
              ['站台損耗合計', kgUnit(chain.data.lossKg, 1)],
            ]} />
            {/* §9 規則 13 的註明 */}
            {chain.data.denominatorNote && (
              <div className="mt-3">
                <Alert tone="warn" title="分母說明">{chain.data.denominatorNote}</Alert>
              </div>
            )}
          </>
        )}
      </Card>

      <Card title="在庫（依物料）">
        {byMaterial.size === 0 ? <Empty>目前沒有在庫料件。</Empty> : (
          <Table head={['物料', '重量', '料件數', '涵蓋片數']}>
            {[...byMaterial.entries()].map(([code, e]) => (
              <tr key={code}>
                <Td>{dict.material(code)}</Td>
                <Td className="tabular-nums">{kgUnit(e.kg, 1)}</Td>
                <Td className="tabular-nums">{count(e.items)}</Td>
                <Td className="tabular-nums">{count(e.panels)}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  )
}

function Stat({ label, value, tone = 'ink' }: { label: string; value: string; tone?: 'ink' | 'sky' }) {
  return (
    <div className={`rounded-lg border p-3 ${tone === 'sky' ? 'border-sky-200 bg-sky-50' : 'border-ink-200 bg-ink-50'}`}>
      <p className="text-xs text-ink-500">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums text-ink-900">{value}</p>
    </div>
  )
}
